import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Input, Spin, Switch } from 'antd'
import dayjs from 'dayjs'
import CalendarDatePicker from 'pages/todo/components/DatePicker'
import { useTranslation } from 'react-i18next'

import CustomDatePicker from './components/CustomDatePicker'

import noteIcon from 'assets/images/calendar/note-icon.svg'
import scheduleIcon from 'assets/images/calendar/property-schedule.svg'

import calendarIcon from 'assets/images/calendar/icon-calendar-24.svg'
import styles from './style.module.scss'
import { Loading3QuartersOutlined } from '@ant-design/icons'

const { TextArea } = Input

interface AutomaticReplyData {
  enabled?: boolean
  subject?: string
  since?: string | Date
  until?: string | Date | null
  message?: string
}

interface AutomaticReplyEditorProps {
  data?: AutomaticReplyData
  onSave: () => void
  onCancel: () => void
}

export interface AutomaticReplyEditorRef {
  getData: () => {
    enabled: boolean
    subject: string
    since: string | Date
    until: string | Date | null
    message: string
  }
  setIsLoading: (loading: boolean) => void
}

const AutomaticReplyEditor = forwardRef<AutomaticReplyEditorRef, AutomaticReplyEditorProps>(
  ({ data, onSave, onCancel }, ref) => {
    const getInitDate = (): Date => {
      const now = new Date()
      const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours())
      return date
    }

    const [isEnabled, setIsEnabled] = useState<boolean>(data?.enabled || false)
    const [title, setTitle] = useState<string>(data?.subject || '')
    const [since, setSince] = useState<string | Date>(data?.since || getInitDate())
    const [until, setUntil] = useState<string | Date | null>(data?.until || null)
    const [message, setMessage] = useState<string>(data?.message || '')
    const [hasEndDate, setHasEndDate] = useState<boolean>(!!data?.until)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [sinceDatePickerOpen, setSinceDatePickerOpen] = useState<boolean>(false)
    const [untilDatePickerOpen, setUntilDatePickerOpen] = useState<boolean>(false)
    const { t } = useTranslation()

    const handleEnableChange = (checked: boolean): void => {
      setIsEnabled(checked)
    }

    const handleChangeSince = (date: Date): void => {
      const inUntil = until ? new Date(until) : null
      if (inUntil && date > inUntil) {
        const duration = inUntil.getTime() - new Date(since).getTime()
        setUntil(dayjs(date).add(duration).toISOString())
      }
      setSince(date.toISOString())
    }

    useImperativeHandle(ref, () => ({
      getData: () => ({
        enabled: isEnabled,
        subject: title,
        since: since,
        until: hasEndDate ? until : null,
        message: message,
      }),
      setIsLoading: (loading: boolean) => {
        setIsLoading(loading)
      },
    }))

    const sinceTimeProps: any = {
      type: 'since',
      timeValue: since,
      setTimeValue: handleChangeSince,
      data: data,
    }

    const untilTimeProps: any = {
      type: 'until',
      timeValue: until ? dayjs(until) : null,
      setTimeValue: setUntil,
      disabled: !hasEndDate,
      since: since,
      data: data,
    }

    return (
      <Spin indicator={<Loading3QuartersOutlined spin />} spinning={isLoading}>
        <div className={styles.automaticReplyEditor}>
          <div className={styles.automaticReplyEditorContent}>
            <div className={styles.header}>
              <p className={styles.description}>
                {t('settings.accountAutomaticRepliesTab.editorHeadDesc')}
              </p>
              <div className={styles.enableSwitch}>
                <Switch
                  style={{ marginLeft: -2 }}
                  checked={isEnabled}
                  onChange={handleEnableChange}
                />
                <span className={styles.switchDesc}>
                  {t('settings.accountAutomaticRepliesTab.enableAutomaticReplies')}
                </span>
              </div>
            </div>

            {isEnabled && (
              <div className={styles.editorContent}>
                <div className={styles.inputGroup}>
                  <Input
                    placeholder={t('settings.accountAutomaticRepliesTab.editorPlaceholder')}
                    value={title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                  />
                </div>

                <div className={styles.dateTimeSectionContainer}>
                  <img src={scheduleIcon} alt="schedule" />
                  <div className={styles.dateTimeSectionsWrapper}>
                    <div className={styles.dateTimeSection}>
                      <div className={styles.datePickerContainer}>
                        <button
                          className={styles.dateButton}
                          onClick={() => setSinceDatePickerOpen(!sinceDatePickerOpen)}
                        >
                          <span className={styles.dateValue}>
                            {dayjs(since).format('YYYY-MM-DD')}
                          </span>
                          <img
                            src={calendarIcon}
                            alt="Calendar"
                            className={styles.datePickerIcon}
                          />
                        </button>
                        {sinceDatePickerOpen && (
                          <div
                            className={styles.datePickerOverlay}
                            onClick={() => setSinceDatePickerOpen(false)}
                          >
                            <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                              <CalendarDatePicker
                                value={new Date(since)}
                                onChange={(date: Date) => {
                                  const newSince = new Date(since)
                                  newSince.setDate(date.getDate())
                                  newSince.setMonth(date.getMonth())
                                  newSince.setFullYear(date.getFullYear())
                                  handleChangeSince(newSince)
                                  setSinceDatePickerOpen(false)
                                }}
                                onClose={() => setSinceDatePickerOpen(false)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* <TimePicker
                      format="HH:mm"
                      value={dayjs(since)}
                      minDate={dayjs().subtract(1, 'hour')}
                      allowClear={false}
                      onChange={(time) => {
                        if (time) {
                          handleChangeSince(time)
                        }
                      }}
                    /> */}
                      <CustomDatePicker {...sinceTimeProps} />
                    </div>

                    <div className={styles.dateTimeSection}>
                      <div className={styles.datePickerContainer}>
                        <button
                          className={styles.dateButton}
                          onClick={() => setUntilDatePickerOpen(!untilDatePickerOpen)}
                          disabled={!hasEndDate}
                        >
                          <span className={styles.dateValue}>
                            {until ? dayjs(until).format('YYYY-MM-DD') : '-'}
                          </span>
                          <img
                            src={calendarIcon}
                            alt="Calendar"
                            className={styles.datePickerIcon}
                          />
                        </button>
                        {untilDatePickerOpen && hasEndDate && (
                          <div
                            className={styles.datePickerOverlay}
                            onClick={() => setUntilDatePickerOpen(false)}
                          >
                            <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                              <CalendarDatePicker
                                value={until ? new Date(until) : null}
                                onChange={(date: Date) => {
                                  const newUntil = new Date(until || date)
                                  newUntil.setDate(date.getDate())
                                  newUntil.setMonth(date.getMonth())
                                  newUntil.setFullYear(date.getFullYear())
                                  setUntil(newUntil.toISOString())
                                  setUntilDatePickerOpen(false)
                                }}
                                onClose={() => setUntilDatePickerOpen(false)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* <TimePicker
                      format="HH:mm"
                      placeholder="-"
                      value={until ? dayjs(until) : null}
                      minDate={dayjs(since)}
                      allowClear={false}
                      onChange={(time) => {
                        if (time) {
                          setUntil(time.toISOString())
                        }
                      }}
                      disabled={!hasEndDate}
                    /> */}
                      <CustomDatePicker {...untilTimeProps} />

                      <Switch
                        checked={hasEndDate}
                        onChange={(checked: boolean) => {
                          setHasEndDate(checked)
                          if (checked) {
                            setUntil(dayjs(since).add(1, 'day').toISOString())
                          } else {
                            setUntil(null)
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.messageGroup}>
                  <img src={noteIcon} alt="note" />
                  <div>
                    <label>{t('settings.accountAutomaticRepliesTab.editorMessage')}</label>
                    <TextArea
                      className={styles.messageTextArea}
                      rows={6}
                      placeholder={t(
                        'settings.accountAutomaticRepliesTab.editorMessagePlaceholder',
                      )}
                      value={message}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setMessage(e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.footer}>
            <button className={styles.cancelButton} type="button" onClick={onCancel}>
              {t('common.button.cancel')}
            </button>

            <button className={styles.saveButton} type="button" onClick={onSave}>
              {t('common.button.save')}
            </button>
          </div>
        </div>
      </Spin>
    )
  },
)

AutomaticReplyEditor.displayName = 'AutomaticReplyEditor'

export default AutomaticReplyEditor
